import { GoogleAuthProvider, OAuthProvider, signInWithPopup, getMultiFactorResolver } from "firebase/auth";

import { auth } from "../../firebase";

/**
 * SSO for the registration flow.
 *
 * Unlike the login thunks in `authentication.actions.js` (which run the popup and
 * then immediately `processLogin` an existing platform account), registration only
 * needs to authenticate the identity and hand back the Firebase token + profile.
 * The platform account is created by the registration API call afterwards — that
 * call is also what decides "known identity → log into existing account".
 *
 * If the account has 2FA enabled (only possible for an existing user), the popup throws
 * `auth/multi-factor-auth-required`; we stash the resolver on `window.__mfaResolver`
 * (same convention as the login thunks) and return `{ mfaRequired: true }` so the flow
 * can show the 2FA code input and complete via the shared `resolveMFA` action.
 *
 * @returns {Promise<{provider: string, idToken: string, expirationTime: string,
 *   refreshToken: string, email: string|null, displayName: string|null} | {mfaRequired: true}>}
 */
const runSsoPopup = async (providerId, provider) => {
    auth.tenantId = process.env.REACT_APP_AUTH_TENANT_ID;

    let result;
    try {
        result = await signInWithPopup(auth, provider);
    } catch (error) {
        if (error?.code === "auth/multi-factor-auth-required") {
            window.__mfaResolver = getMultiFactorResolver(auth, error);
            return { mfaRequired: true };
        }
        throw error;
    }

    const idToken = await result.user.getIdToken();
    const tokenResult = await result.user.getIdTokenResult();

    return {
        provider: providerId,
        idToken,
        expirationTime: tokenResult.expirationTime,
        refreshToken: result.user.refreshToken,
        email: result.user.email,
        displayName: result.user.displayName,
    };
};

export const startGoogleRegistration = () => runSsoPopup("GOOGLE", new GoogleAuthProvider());

export const startMicrosoftRegistration = () => {
    const provider = new OAuthProvider("microsoft.com");
    provider.addScope("openid");
    provider.addScope("profile");
    provider.addScope("email");
    return runSsoPopup("MICROSOFT", provider);
};

// Map Firebase SSO errors to user-friendly German messages. Returns null when the
// user simply closed/cancelled the popup (no error UI needed in that case).
export const mapSsoError = (error) => {
    switch (error?.code) {
        case "auth/popup-closed-by-user":
        case "auth/cancelled-popup-request":
            return null;
        case "auth/popup-blocked":
            return "Pop-up wurde blockiert. Bitte erlaube Pop-ups für diese Seite und versuche es erneut.";
        case "auth/account-exists-with-different-credential":
            return "Für diese E-Mail-Adresse existiert bereits ein Konto mit einer anderen Anmeldemethode. Bitte melde dich zuerst mit deiner bisherigen Methode an.";
        case "auth/network-request-failed":
            return "Netzwerkfehler bei der Anmeldung. Bitte überprüfe deine Internetverbindung und versuche es erneut.";
        default:
            return "Bei der Registrierung mit SSO ist ein Fehler aufgetreten. Bitte versuche es erneut oder kontaktiere onboarding@pathway-solutions.de.";
    }
};
